import { buildInterviewMoment } from 'features/sendouts/common/utils';
import moment from 'moment-timezone';
import strings from 'strings';
import { prepareFormToSubmit } from 'UI/utils/forms';

import { buildResolvedEmailBody } from '../../forms/EmailDetails/EmailDetails.utils';
import { TIME_FRAME_MAP } from '../../forms/InterviewSchedule/InterviewSchedule.constants';
import { OTHER_INTERVIEWER_FORM_FIELDS_MAP } from '../../shared';

import {
  FORM_FIELDS_MAP,
  SENDOUT_CREATION_EMAIL_FIELDS_MAP,
  SENDOUT_CREATION_FIELDS_MAP
} from './SendoutForm/SendoutForm.constants';
import { ATTACHMENT_FIELD_KEYS } from './SendoutForm/SendoutForm.utils';

const FIXED_HOUR_PAST_TIME_ERROR = strings.sendouts.form.errors.fixedHourPastTime;
const TIME_RANGE_PAST_START_TIME_ERROR = strings.sendouts.form.errors.timeRangePastStartTime;
const INVALID_INTERVIEW_TIME_ERROR = strings.sendouts.form.errors.invalidInterviewTime;

const V2_TIME_FRAMES = {
  [TIME_FRAME_MAP.FixedHour.id]: 'fixedHour',
  [TIME_FRAME_MAP.TimeRange.id]: 'range'
};

export const getSelectedAttachments = formData =>
  ATTACHMENT_FIELD_KEYS.flatMap(fieldKey => formData?.[fieldKey] || []);

const getPastInterviewTimeError = ({
  formData = {},
  expectedTimeFrameId,
  timeFieldKey,
  errorMessage
}) => {
  const interviewTimeFrame = formData[FORM_FIELDS_MAP.InterviewTimeFrame.key];
  const interviewDate = formData[FORM_FIELDS_MAP.InterviewDate.key];
  const interviewTime = formData[timeFieldKey];
  const interviewTimezone = formData[FORM_FIELDS_MAP.InterviewTimezone.key];
  const timezoneName = interviewTimezone?.momentName;

  if (interviewTimeFrame?.id !== expectedTimeFrameId) return null;

  const selectedDateTime = buildInterviewMoment({
    interviewDate,
    time: interviewTime,
    timezoneName
  });

  if (!selectedDateTime) return null;

  const nowInTimezone = moment().tz(timezoneName);

  if (!selectedDateTime.isSame(nowInTimezone, 'day')) return null;

  return selectedDateTime.isAfter(nowInTimezone) ? null : errorMessage;
};

export const getFixedHourInterviewTimeError = (formData = {}) =>
  getPastInterviewTimeError({
    formData,
    expectedTimeFrameId: TIME_FRAME_MAP.FixedHour.id,
    timeFieldKey: FORM_FIELDS_MAP.InterviewFromHour.key,
    errorMessage: FIXED_HOUR_PAST_TIME_ERROR
  });

export const getTimeRangeInterviewStartTimeError = (formData = {}) =>
  getPastInterviewTimeError({
    formData,
    expectedTimeFrameId: TIME_FRAME_MAP.TimeRange.id,
    timeFieldKey: FORM_FIELDS_MAP.InterviewFromHour.key,
    errorMessage: TIME_RANGE_PAST_START_TIME_ERROR
  });

const getInvalidInterviewTimeValidationError = ({
  formData = {},
  expectedTimeFrameId,
  timeFieldKey
}) => {
  const interviewTimeFrame = formData[FORM_FIELDS_MAP.InterviewTimeFrame.key];
  const interviewDate = formData[FORM_FIELDS_MAP.InterviewDate.key];
  const interviewTimezone = formData[FORM_FIELDS_MAP.InterviewTimezone.key];
  const time = formData[timeFieldKey];

  if (
    interviewTimeFrame?.id !== expectedTimeFrameId ||
    !interviewDate ||
    !interviewTimezone?.momentName ||
    !time
  ) {
    return null;
  }

  return buildInterviewMoment({
    interviewDate,
    time,
    timezoneName: interviewTimezone.momentName
  })
    ? null
    : { fieldKey: timeFieldKey, message: INVALID_INTERVIEW_TIME_ERROR };
};

export const getInterviewTimeValidationError = (formData = {}) => {
  const fixedHourInvalidTimeError = getInvalidInterviewTimeValidationError({
    formData,
    expectedTimeFrameId: TIME_FRAME_MAP.FixedHour.id,
    timeFieldKey: FORM_FIELDS_MAP.InterviewFromHour.key
  });

  if (fixedHourInvalidTimeError) return fixedHourInvalidTimeError;

  const timeRangeStartInvalidTimeError = getInvalidInterviewTimeValidationError({
    formData,
    expectedTimeFrameId: TIME_FRAME_MAP.TimeRange.id,
    timeFieldKey: FORM_FIELDS_MAP.InterviewFromHour.key
  });

  if (timeRangeStartInvalidTimeError) return timeRangeStartInvalidTimeError;

  const timeRangeEndInvalidTimeError = getInvalidInterviewTimeValidationError({
    formData,
    expectedTimeFrameId: TIME_FRAME_MAP.TimeRange.id,
    timeFieldKey: FORM_FIELDS_MAP.InterviewToHour.key
  });

  if (timeRangeEndInvalidTimeError) return timeRangeEndInvalidTimeError;

  const fixedHourInterviewTimeError = getFixedHourInterviewTimeError(formData);

  if (fixedHourInterviewTimeError) {
    return {
      fieldKey: FORM_FIELDS_MAP.InterviewFromHour.key,
      message: fixedHourInterviewTimeError
    };
  }

  const timeRangeInterviewStartTimeError = getTimeRangeInterviewStartTimeError(formData);

  if (timeRangeInterviewStartTimeError) {
    return {
      fieldKey: FORM_FIELDS_MAP.InterviewFromHour.key,
      message: timeRangeInterviewStartTimeError
    };
  }

  return null;
};

export const buildSendoutInterviewPayload = (formData = {}) => {
  const timezone = formData?.interviewTimezone?.momentName ?? null;
  const timeFrame = V2_TIME_FRAMES[formData?.interviewTimeFrame?.id] ?? null;
  const buildMoment = time =>
    buildInterviewMoment({ interviewDate: formData?.interviewDate, time, timezoneName: timezone });
  const startMoment = buildMoment(formData?.interviewFromHour);
  const endMoment = timeFrame === 'range' ? buildMoment(formData?.interviewToHour) : null;

  const {
    full_name: fullName,
    email,
    cc_emails: ccEmails
  } = prepareFormToSubmit(formData, OTHER_INTERVIEWER_FORM_FIELDS_MAP);

  return {
    interviewTypeId: formData?.interviewType ?? null,
    timeFrame,
    startTimestamp: startMoment ? startMoment.toISOString() : null,
    endTimestamp: endMoment ? endMoment.toISOString() : null,
    timezone,
    sendReminders: !formData?.dontSendReminders,
    address: formData?.interviewAddress ?? null,
    link: formData?.interviewLink ?? null,
    ccEmails,
    otherInterviewer: {
      email,
      fullName
    }
  };
};

export const prepareSendoutPayload = ({ formData = {} }) => {
  const preparedFormData = prepareFormToSubmit(formData, SENDOUT_CREATION_FIELDS_MAP);

  return {
    ...preparedFormData,
    interview: buildSendoutInterviewPayload(formData),
    ...(preparedFormData.sendEmailToHiringAuthority
      ? { email: prepareFormToSubmit(formData, SENDOUT_CREATION_EMAIL_FIELDS_MAP) }
      : {})
  };
};

export const buildSendoutSubmissionData = ({
  formData = {},
  jobOrder = {},
  candidateInfo = {}
}) => {
  const { resolvedEmailBody, resolvedTagValues } = buildResolvedEmailBody({
    emailBody: formData[FORM_FIELDS_MAP.EmailBody.key],
    hiringAuthority: formData[FORM_FIELDS_MAP.HiringAuthority.key],
    candidate: formData[FORM_FIELDS_MAP.Candidate.key],
    interviewData: {
      interviewDate: formData[FORM_FIELDS_MAP.InterviewDate.key],
      interviewFromHour: formData[FORM_FIELDS_MAP.InterviewFromHour.key],
      interviewToHour: formData[FORM_FIELDS_MAP.InterviewToHour.key],
      interviewTimeFrame: formData[FORM_FIELDS_MAP.InterviewTimeFrame.key],
      interviewTimezone: formData[FORM_FIELDS_MAP.InterviewTimezone.key]
    },
    jobOrder,
    candidateInfo
  });

  return {
    payload: prepareSendoutPayload({ formData }),
    confirmation: {
      resolvedEmailBody,
      resolvedTagValues
    }
  };
};
